import { calculateProductValues } from 'helpers/unitConversionHelpers';
import { Product } from 'screens/Applications&WorkOrders/StepsLayout/Constants/formikData';
import { Maths } from 'helpers/maths';
import { ProductQuantityType } from 'screens/Applications&WorkOrders/Applications/Hooks/useApplication';

/**
 * Calculate product values based on rate per acre and applied acres
 * @param ratePerAcre - Rate per acre value
 * @param appliedAcres - Applied acres value
 * @param rateUnit - Unit for rate per acre
 * @param product - Complete form values containing product data
 * @returns Complete calculated product values object
 */
export const calculateProductValuesFromRate = (
  ratePerAcre: number,
  appliedAcres: number,
  rateUnit: string,
  product: Product,
  values?: { tankInformation?: { tankCount?: number } },
  convertFn?: Function
): any => {
  if (ratePerAcre < 0 || appliedAcres <= 0) {
    return null;
  }

  return calculateProductValues(
    'ratePerAreaValue',
    ratePerAcre,
    {
      rateUnit,
      totalProdUnit: product.totalProductUnit,
      tankUnit: product.ratePerTankUnit
    },
    { changedArea: appliedAcres },
    null,
    product,
    { values, convertFn }
  );
};

/**
 * Calculate product values based on rate per tank and applied acres
 * @param ratePerTank - Rate per tank value
 * @param appliedAcres - Applied acres value
 * @param tankUnit - Unit for rate per tank
 * @param product - Complete form values containing product data
 * @param values - Optional tank information
 * @returns Complete calculated product values object
 */
export const calculateProductValuesFromTank = (
  ratePerTank: number,
  appliedAcres: number,
  tankUnit: string,
  product: Product,
  values?: { tankInformation?: { tankCount?: number } },
  convertFn?: Function
): any => {
  if (ratePerTank < 0 || appliedAcres <= 0) {
    return null;
  }

  return calculateProductValues(
    'ratePerTankValue',
    ratePerTank,
    {
      tankUnit,
      rateUnit: product.ratePerAreaUnit,
      totalProdUnit: product.totalProductUnit
    },
    { changedArea: appliedAcres },
    null,
    product,
    { values, convertFn }
  );
};

/**
 * Calculate product values based on total product and applied acres
 * @param totalProduct - Total product value
 * @param appliedAcres - Applied acres value
 * @param totalUnit - Unit for total product
 * @param product - Complete form values containing product data
 * @returns Complete calculated product values object
 */
export const calculateProductValuesFromTotal = (
  totalProduct: number,
  appliedAcres: number,
  totalUnit: string,
  product: Product,
  values?: { tankInformation?: { tankCount?: number } },
  convertFn?: Function
): any => {
  if (totalProduct < 0 || appliedAcres <= 0) {
    return null;
  }

  return calculateProductValues(
    'totalProductValue',
    totalProduct,
    {
      rateUnit: product.ratePerAreaUnit,
      tankUnit: product.ratePerTankUnit,
      totalProdUnit: totalUnit
    },
    { changedArea: appliedAcres },
    null,
    product,
    { values, convertFn }
  );
};

export const calculateTotalCost = (products: Product[]): number => {
  return products.reduce((total, product) => {
    return total + (product.totalCost || 0);
  }, 0);
};

export const calculateCostPerUnit = (
  totalCost: number,
  totalProduct: number
) => {
  return Maths.divideSafeRound(totalCost, totalProduct);
};

export const calculateProductByQuantityMode = (
  quantityMode: keyof typeof ProductQuantityType,
  newAppliedAcres: number,
  product: Product,
  values?: { tankInformation?: { tankCount?: number } },
  convertFn?: Function
): any => {
  switch (quantityMode) {
    case 'ratePerTank':
      return calculateProductValuesFromTank(
        product.ratePerTank || 0,
        newAppliedAcres,
        product.ratePerTankUnit || '',
        product,
        values,
        convertFn
      );
    case 'totalProduct':
      return calculateProductValuesFromTotal(
        product.totalProduct || 0,
        newAppliedAcres,
        product.totalProductUnit || '',
        product,
        values,
        convertFn
      );
    default:
      return calculateProductValuesFromRate(
        product.ratePerAcre || 0,
        newAppliedAcres,
        product.ratePerAreaUnit || '',
        product,
        values,
        convertFn
      );
  }
};
